import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-store';

/**
 * Gates a subtree on authentication.
 *   - loading      → render a quiet skeleton (no flash of content)
 *   - anonymous    → redirect to /auth/sign-in, preserving the original path
 *                    so we can land them back where they wanted after sign-in
 *   - authenticated → render children
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-live="polite">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span
            className="inline-block h-2.5 w-2.5 animate-mascot-blink rounded-full bg-accent"
            aria-hidden
          />
          Loading your space&hellip;
        </div>
      </div>
    );
  }

  if (state.status === 'anonymous') {
    return <Navigate to="/auth/sign-in" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
